import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Avatar,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Stack,
    Paper,
    Tabs,
    Tab,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondary,
    DialogContentText,
    Tooltip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import api from '../../services/api';
import { Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon, Search as SearchIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';

const StatusCard = ({ title, isActive, onToggle, loading }) => (
    <Paper sx={{ p: 2, mt: 2, bgcolor: isActive ? 'primary.50' : 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs>
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 'medium' }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {isActive 
                        ? "This client IS registered as a coach or player."
                        : "This client IS NOT registered as a coach or player."}
                </Typography>
            </Grid>
            <Grid item>
                <Button
                    variant={isActive ? "outlined" : "contained"}
                    onClick={onToggle}
                    disabled={loading}
                    color={isActive ? "error" : "primary"}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                >
                    {isActive ? "Remove Status" : "Set as Coach/Player"}
                </Button>
            </Grid>
        </Grid>
    </Paper>
);

const ClientDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [updatingCoaching, setUpdatingCoaching] = useState(false);
    const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);
    const [newChildData, setNewChildData] = useState({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: ''
    });
    const [addingChild, setAddingChild] = useState(false);
    const [removingChildId, setRemovingChildId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [childToRemove, setChildToRemove] = useState(null);
    const [searchChildDialogOpen, setSearchChildDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess(null);
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    // Get coaching/playing status from our database
    const isCoachingOrPlaying = useMemo(() => {
        if (!clientData) return false;
        return clientData.isCoachingOrPlaying || false;
    }, [clientData]);

    const fetchClientData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/admin/clients/db/${id}`);
            setClientData(response.data.client);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to fetch client data');
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Fetch client data from our database
    useEffect(() => {
        if (id) {
            fetchClientData();
        }
    }, [id, fetchClientData]);

    const handleToggleCoaching = async () => {
        try {
            setUpdatingCoaching(true);
            setError(null);
            setSuccess(null);
            
            const response = await api.put(`/admin/clients/${id}/coaching-playing`, {
                enabled: !isCoachingOrPlaying
            });
            
            setClientData(response.data.client);
            setSuccess(response.data.message || `Successfully ${!isCoachingOrPlaying ? 'enabled' : 'disabled'} coaching/playing status`);
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to update coaching status');
        } finally {
            setUpdatingCoaching(false);
        }
    };

    const formatDate = (dateString, includeTime = false) => {
        if (!dateString) return 'N/A';
        const dt = DateTime.fromISO(dateString);
        // For date-only fields, use UTC to avoid timezone shift (midnight UTC → previous day in local)
        if (!includeTime) {
            return dt.toUTC().toLocaleString(DateTime.DATE_SHORT);
        }
        return dt.toLocaleString(DateTime.DATETIME_SHORT);
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phone;
    };

    const handleAddChild = async () => {
        try {
            setAddingChild(true);
            setError(null);
            setSuccess(null);

            // First, try to find the child in our database
            const searchResponse = await api.get('/admin/children/search', {
                params: {
                    query: `${newChildData.firstName} ${newChildData.lastName}`,
                    dateOfBirth: newChildData.dateOfBirth
                }
            });

            // If we found an exact match, show error
            const existingChild = searchResponse.data.children?.find(child => 
                child.firstName.toLowerCase() === newChildData.firstName.toLowerCase() &&
                child.lastName.toLowerCase() === newChildData.lastName.toLowerCase() &&
                child.dateOfBirth === newChildData.dateOfBirth
            );

            if (existingChild) {
                if (existingChild.isClaimed) {
                    throw new Error(`A child with these details already exists and is linked to ${existingChild.parentInfo.name}`);
                } else {
                    // If child exists but isn't claimed, we can link it
                    await api.post(`/admin/clients/${id}/children/${existingChild._id}`);
                    await fetchClientData();
                    setAddChildDialogOpen(false);
                    setNewChildData({
                        firstName: '',
                        lastName: '',
                        dateOfBirth: '',
                        gender: ''
                    });
                    setSuccess('Successfully linked existing child');
                    return;
                }
            }

            // Create new child record with parent link
            const createResponse = await api.post('/admin/clients/children', {
                ...newChildData,
                parentId: id
            });

            await fetchClientData();
            setAddChildDialogOpen(false);
            setNewChildData({
                firstName: '',
                lastName: '',
                dateOfBirth: '',
                gender: ''
            });
            setSuccess(createResponse.data.message || 'Successfully added child');
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to add child');
        } finally {
            setAddingChild(false);
        }
    };

    const handleRemoveChildClick = (child) => {
        setChildToRemove(child);
        setConfirmDialogOpen(true);
    };

    const handleConfirmRemove = async () => {
        if (!childToRemove) return;
        
        try {
            setRemovingChildId(childToRemove._id);
            setError(null);
            setSuccess(null);

            const response = await api.delete(`/admin/clients/${id}/children/${childToRemove._id}`);
            await fetchClientData();
            setSuccess(response.data.message || 'Successfully removed child');
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to remove child');
        } finally {
            setRemovingChildId(null);
            setChildToRemove(null);
            setConfirmDialogOpen(false);
        }
    };

    const handleCancelRemove = () => {
        setChildToRemove(null);
        setConfirmDialogOpen(false);
    };

    const handleSearchChildren = async () => {
        if (!searchQuery.trim()) return;

        try {
            setSearching(true);
            setError(null);
            
            const response = await api.get('/admin/children/search', {
                params: { query: searchQuery }
            });
            
            setSearchResults(response.data.children || []);
        } catch (error) {
            setError('Failed to search for children');
        } finally {
            setSearching(false);
        }
    };

    const handleLinkExistingChild = async (childId) => {
        try {
            setSearching(true);
            setError(null);
            setSuccess(null);

            const response = await api.post(`/admin/clients/${id}/children/${childId}`);
            await fetchClientData();
            setSearchChildDialogOpen(false);
            setSearchQuery('');
            setSearchResults([]);
            setSuccess(response.data.message || 'Successfully linked child');
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to link child');
        } finally {
            setSearching(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (!clientData) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 3 }}>
                    Client not found
                </Alert>
                <Button onClick={() => navigate('/admin/clients')}>
                    Back to Clients
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Button onClick={() => navigate('/admin/clients')} sx={{ mb: 3 }}>
                Back to Clients
            </Button>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Grid container spacing={3} sx={{ p: 3 }}>
                    <Grid item xs={12} sm={4} display="flex" justifyContent="center">
                        <Avatar
                            alt={`${clientData?.firstName} ${clientData?.lastName}`}
                            src={clientData?.profilePicture}
                            sx={{ 
                                width: 150, 
                                height: 150,
                                bgcolor: theme.palette.primary.main
                            }}
                        >
                            {!clientData?.profilePicture && `${clientData?.firstName?.[0]}${clientData?.lastName?.[0]}`}
                        </Avatar>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Typography variant="h5">
                                {clientData?.firstName} {clientData?.lastName}
                            </Typography>
                            <Chip
                                label={clientData?.active ? 'Active' : 'Inactive'}
                                color={clientData?.active ? 'success' : 'default'}
                                size="small"
                            />
                            {clientData?.ezFacilityClientId && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<OpenInNewIcon />}
                                    onClick={() => window.open(`https://tms.ezfacility.com/ClientDetails.aspx?ClientID=${clientData.ezFacilityClientId}`, '_blank')}
                                >
                                    View in EZFacility
                                </Button>
                            )}
                        </Box>
                        <Typography color="textSecondary" gutterBottom>
                            <b>Username:</b> {clientData?.ssUsername}
                        </Typography>
                        
                        <StatusCard
                            title="Coach/Player Status"
                            isActive={isCoachingOrPlaying}
                            onToggle={handleToggleCoaching}
                            loading={updatingCoaching}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper>
                <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Children" />
                    <Tab label="Membership" />
                    <Tab label="Contact Info" />
                    <Tab label="Additional Info" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {activeTab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">Children</Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            startIcon={<SearchIcon />}
                                            onClick={() => setSearchChildDialogOpen(true)}
                                            variant="outlined"
                                        >
                                            Link Existing Child
                                        </Button>
                                        <Button
                                            startIcon={<AddIcon />}
                                            onClick={() => setAddChildDialogOpen(true)}
                                            variant="contained"
                                            color="primary"
                                        >
                                            Create New Child
                                        </Button>
                                    </Stack>
                                </Box>
                                {clientData.children?.length > 0 ? (
                                    <List>
                                        {clientData.children.map((child) => (
                                            <ListItem
                                                key={child._id}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="remove"
                                                        onClick={() => handleRemoveChildClick(child)}
                                                        disabled={removingChildId === child._id}
                                                    >
                                                        {removingChildId === child._id ? (
                                                            <CircularProgress size={24} />
                                                        ) : (
                                                            <DeleteIcon />
                                                        )}
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={`${child.firstName} ${child.lastName}`}
                                                    secondary={`Born: ${formatDate(child.dateOfBirth)} | Gender: ${child.gender}`}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography color="textSecondary">
                                        No children found
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 1 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Membership Details</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Type</Typography>
                                        <Typography gutterBottom>{clientData.membershipType || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Classification</Typography>
                                        <Typography gutterBottom>{clientData.membershipClassification || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Number</Typography>
                                        <Typography gutterBottom>{clientData.membershipNumber || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Contract ID</Typography>
                                        <Typography gutterBottom>{clientData.membershipContractId || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Contract Status</Typography>
                                        <Typography gutterBottom>{clientData.membershipContractStatus || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Status Start Date</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.membershipContractStatusStartDate)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Start Date</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.membershipStartDate)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">End Date</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.membershipEndDate)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Unlimited Visits</Typography>
                                        <Typography gutterBottom>{clientData.membershipHasUnlimitedVisits ? 'Yes' : 'No'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Max Visits</Typography>
                                        <Typography gutterBottom>{clientData.membershipMaxVisits || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Visits Count</Typography>
                                        <Typography gutterBottom>{clientData.membershipVisitsCount || '0'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Visits Remaining</Typography>
                                        <Typography gutterBottom>{clientData.membershipVisitsRemaining || '0'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Amount Due</Typography>
                                        <Typography gutterBottom>${clientData.amountDue?.toFixed(2) || '0.00'}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 2 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Contact Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Email</Typography>
                                        <Typography gutterBottom>
                                            {Array.isArray(clientData.email) ? clientData.email.join(', ') : clientData.email || 'N/A'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Cell Phone</Typography>
                                        <Typography gutterBottom>{formatPhoneNumber(clientData.phoneCell)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Home Phone</Typography>
                                        <Typography gutterBottom>{formatPhoneNumber(clientData.phoneHome)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Work Phone</Typography>
                                        <Typography gutterBottom>{formatPhoneNumber(clientData.phoneWork)}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Address</Typography>
                                        <Typography gutterBottom>
                                            {clientData.address1 || 'N/A'}
                                            {clientData.city && clientData.stateProv && (
                                                <>
                                                    <br />
                                                    {clientData.city}, {clientData.stateProv} {clientData.postalCode}
                                                </>
                                            )}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Email Subscriptions</Typography>
                                        {clientData.emailSubscriptions?.map((sub, index) => (
                                            <Box key={index} sx={{ ml: 2, mb: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>{sub.emailAddress}</strong> ({sub.emailVerificationStatus})
                                                </Typography>
                                                {sub.subscriptions?.map((s, i) => (
                                                    <Typography key={i} variant="body2" color="textSecondary">
                                                        • {s.subscription}: {s.optIn ? 'Opted In' : 'Opted Out'}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {activeTab === 3 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Additional Information</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Username</Typography>
                                        <Typography gutterBottom>{clientData.ssUsername || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">EZFacility ID</Typography>
                                        <Typography gutterBottom>{clientData.ezFacilityClientId || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Gender</Typography>
                                        <Typography gutterBottom>{clientData.gender || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Account Status</Typography>
                                        <Typography gutterBottom>{clientData.active ? 'Active' : 'Inactive'}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Date Created</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.dateCreated, true)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Last Modified</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.lastModified, true)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Last Synced</Typography>
                                        <Typography gutterBottom>{formatDate(clientData.lastSyncedAt, true)}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" sx={{ mt: 2 }}>Custom Fields</Typography>
                                        {clientData.customFields?.map((field, index) => (
                                            <Box key={index} sx={{ ml: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>{field.name}:</strong> {field.value || 'N/A'}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Paper>

            {/* Add Child Dialog */}
            <Dialog open={addChildDialogOpen} onClose={() => setAddChildDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Child</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="First Name"
                            value={newChildData.firstName}
                            onChange={(e) => setNewChildData(prev => ({ ...prev, firstName: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Last Name"
                            value={newChildData.lastName}
                            onChange={(e) => setNewChildData(prev => ({ ...prev, lastName: e.target.value }))}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Date of Birth"
                            type="date"
                            value={newChildData.dateOfBirth}
                            onChange={(e) => setNewChildData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Gender"
                            select
                            value={newChildData.gender}
                            onChange={(e) => setNewChildData(prev => ({ ...prev, gender: e.target.value }))}
                            fullWidth
                            required
                            SelectProps={{ native: true }}
                        >
                            <option value="">Select gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="other">Other</option>
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddChildDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleAddChild}
                        disabled={addingChild || !newChildData.firstName || !newChildData.lastName || !newChildData.dateOfBirth || !newChildData.gender}
                        variant="contained"
                    >
                        {addingChild ? <CircularProgress size={24} /> : 'Add Child'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCancelRemove}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Remove Child
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to remove {childToRemove?.firstName} {childToRemove?.lastName} from this parent's profile?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRemove} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmRemove} 
                        color="error" 
                        variant="contained" 
                        autoFocus
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Search Child Dialog */}
            <Dialog 
                open={searchChildDialogOpen} 
                onClose={() => {
                    setSearchChildDialogOpen(false);
                    setSearchQuery('');
                    setSearchResults([]);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Link Existing Child</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Search Children"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            placeholder="Enter name to search..."
                            InputProps={{
                                endAdornment: (
                                    <IconButton 
                                        onClick={handleSearchChildren}
                                        disabled={searching || !searchQuery.trim()}
                                    >
                                        {searching ? <CircularProgress size={24} /> : <SearchIcon />}
                                    </IconButton>
                                )
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearchChildren();
                                }
                            }}
                        />
                        {searchResults.length > 0 ? (
                            <List>
                                {searchResults.map((child) => (
                                    <ListItem
                                        key={child._id}
                                        secondaryAction={
                                            !child.isClaimed ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleLinkExistingChild(child._id)}
                                                    disabled={searching}
                                                >
                                                    Link
                                                </Button>
                                            ) : (
                                                <Tooltip title="Click to view parent's profile in new tab">
                                                    <Chip
                                                        label={`Claimed by ${child.parentInfo?.name}`}
                                                        color="default"
                                                        size="small"
                                                        clickable
                                                        onClick={() => window.open(`/admin/clients/${child.parentInfo?.id}`, '_blank')}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                bgcolor: 'action.hover',
                                                            }
                                                        }}
                                                        icon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                    />
                                                </Tooltip>
                                            )
                                        }
                                    >
                                        <ListItemText
                                            primary={`${child.firstName} ${child.lastName}`}
                                            secondary={
                                                <Stack spacing={0.5}>
                                                    <Typography variant="body2">
                                                        Born: {formatDate(child.dateOfBirth)}
                                                    </Typography>
                                                    {child.isClaimed && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Parent: {child.parentInfo?.name} ({child.parentInfo?.username})
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : searchQuery && !searching && (
                            <Alert severity="info">No children found matching your search</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setSearchChildDialogOpen(false);
                            setSearchQuery('');
                            setSearchResults([]);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ClientDetails; 